<template>
  <div>
    <b-overlay
      id="overlay-background"
      :show="loader === true"
      variant="dark"
      opacity="0.85"
      blur="2px"
      rounded="sm"
      spinner-variant="primary"
      spinner-medium
    >
      <b-alert
        variant="danger"
        :show="alertErrors.length > 0"
        class="mb-0"
      >
        <div
          v-for="(row, index) in alertErrors"
          :key="index"
          class="alert-body"
        >
          <feather-icon

            icon="InfoIcon"
            class="mr-50"
          />
          {{ row }}
        </div>
      </b-alert>
      <b-card
        aria-hidden="true"
        class="mb-0"
      >
        <validation-observer
          #default="{ handleSubmit }"
          ref="refFormObserver"
        >

          <!-- User Info: Input Fields -->
          <b-form
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="resetForm"
          >
            <b-row>

              <!-- Field: Username -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Username"
                  rules="required|alpha-num|unique_username"
                >
                  <b-form-group
                    :label="$t('DEBTIA_USERNAME')"
                    label-for="username"
                  >
                    <b-form-input
                      id="username"
                      v-model="userData.username"
                      :state="getValidationState(validationContext)"
                      trim
                      :maxlength="maxCharactersAllowed"
                      @input="characterLeft"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                    <small
                      :style="maxCharacters > 10 ? 'color: green' : 'color: red'"
                    >
                      {{ maxCharacters }} characters left out of {{ maxCharactersAllowed }}
                    </small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Full Name -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Name"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_NAME')"
                    label-for="full-name"
                  >
                    <b-form-input
                      id="full-name"
                      v-model="userData.name"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Email -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Email"
                  rules="required|email|unique_email"
                >
                  <b-form-group
                    :label="$t('DEBTIA_EMAIL')"
                    label-for="email"
                  >
                    <b-form-input
                      id="email"
                      v-model="userData.email"
                      type="email"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Phone -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Phone"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_PHONE')"
                    label-for="phone"
                  >
                    <b-form-input
                      id="phone"
                      v-model="userData.phone"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Password -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Password"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_PASSWORD')"
                    label-for="password"
                  >
                    <b-form-input
                      id="password"
                      v-model="userData.password"
                      :state="getValidationState(validationContext)"
                      trim
                      type="password"
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Address -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="Address"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('DEBTIA_ADDRESS')"
                    label-for="address"
                  >
                    <b-form-input
                      id="address"
                      v-model="userData.address"
                      :state="getValidationState(validationContext)"
                      trim
                    />
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Field: Status -->
              <b-col
                cols="12"
                md="4"
              >

                <b-form-group
                  :label="$t('DEBTIA_LOGIN')"
                  label-for="login-status"
                >
                  <v-select
                    v-model="userData.loginStatus"
                    :v-if="!loading"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="loginOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="login-status"
                  />
                </b-form-group>
              </b-col>

              <!-- Field: Role -->
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_USER_ROLE')"
                  label-for="user-role"
                >
                  <v-select
                    v-model="role"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="roleOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="user-role"
                  />
                </b-form-group>
              </b-col>

              <b-col
                v-if="userType.data.user_type === 'super-admin' || userType.data.user_type === 'system-user'"
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_USER_TYPE')"
                  label-for="user-type"
                >
                  <v-select
                    v-model="userData.user_type"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="typeOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="user-type"
                    :value="1"
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('DEBTIA_ADMIN_SETTINGS')"
                  label-for="user-settings"
                >
                  <v-select
                    v-model="userData.userSettings"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="loginOptions"
                    :reduce="val => val.value"
                    :clearable="false"
                    input-id="user-settings"
                  />
                </b-form-group>
              </b-col>

            </b-row>

            <!-- PERMISSION TABLE -->
            <b-card
              no-body
              class="border mt-1"
            >
              <b-card-header class="p-1">
                <b-card-title class="font-medium-2">
                  <feather-icon
                    icon="LockIcon"
                    size="18"
                  />
                  <span class="align-middle ml-50">{{ $t('DEBTIA_PERMISSIONS') }}</span>
                </b-card-title>
              </b-card-header>
              <b-table
                v-if="!loader"
                striped
                responsive
                class="mb-0"
                :items="permissionsData"
              >
                <template #cell(module)="data">
                  {{ data.value }}
                </template>
                <template #cell()="data">
                  <b-form-checkbox
                    v-model="selected"
                    :checked="data.value"
                    :value="data.value"
                  />
                </template>
              </b-table>
            </b-card>
            <!-- Action Buttons -->
            <b-button
              variant="primary"
              class="mb-1 mb-sm-0 mr-0 mr-sm-1"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
              type="submit"
            >
              {{ $t('DEBTIA_SAVE') }}
            </b-button>
            <b-button
              variant="outline-secondary"
              type="reset"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
            >
              {{ $t('DEBTIA_RESET') }}
            </b-button>
          </b-form>

        </validation-observer>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BButton, BRow, BCol, BFormGroup, BFormInput, BForm, BTable, BCard, BCardHeader, BCardTitle, BFormCheckbox,
  BFormInvalidFeedback, BOverlay, BAlert,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { avatarText } from '@core/utils/filter'
import vSelect from 'vue-select'
import { ref/* , onUnmounted  */ } from '@vue/composition-api'
import axios from '@axios'
import store from '@/store'

import formValidation from '@core/comp-functions/forms/form-validation'
import { required, alphaNum, email } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import userStoreModule from '../userStoreModule'

export default {
  components: {
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BTable,
    BCard,
    BCardHeader,
    BCardTitle,
    BFormCheckbox,
    vSelect,
    // Form Validation
    ValidationProvider,
    ValidationObserver,
    BFormInvalidFeedback,
    BOverlay,
    BAlert,
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      selected: [],
      alertErrors: [],
      userType: JSON.parse(localStorage.getItem('userData')),
      maxCharacters: 50,
      maxCharactersAllowed: 50,
    }
  },
  setup() {
    const roleOptions = []

    const loginOptions = [
      { label: 'Allowed', value: '1' },
      { label: 'Not Allowed', value: '0' },
    ]
    const loading = true
    const loader = true
    const headingArray = []

    const permissionsData = []
    const refInputEl = ref(null)
    const previewEl = ref(null)
    const permissions = []
    const loginOptionsArgs = []

    const requestArgs = [
      { request: '/user/get-login-options', param: 'loginOptions' },
      { request: '/user/get-user-roles', param: 'roleOptions' },
    ]

    const blankUserData = {
      username: '',
      email: '',
      name: '',
      role: null,
      loginStatus: '1',
      generateAgreementPlan: '1',
      cancelAgreementPlan: '1',
      extendPauseDate: '1',
      getEventLogs: '1',
      getOrders: '1',
      userSettings: '1',
      editStatements: '1',
      deleteDebtCaseFile: '1',
      caseProcessChangePermission: '1',
      setBailifAbility: '1',
      stuckCasesPermission: '1',
      phone: null,
      password: null,
      address: null,
      user_type: '2',
      apiTokenRevoke: '1',
      company: null,
    }

    const userData = ref(JSON.parse(JSON.stringify(blankUserData)))
    const resetuserData = () => {
      userData.value = JSON.parse(JSON.stringify(blankUserData))
      // console.log('userData', userData)
    }
    const { role } = userData

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetuserData)

    const typeOptions = [
      { label: 'User', value: '2' },
      { label: 'Company', value: '1' },
    ]

    const apiTokenRevokeOptions = [
      { label: 'Allowed', value: '1' },
      { label: 'Not Allowed', value: '0' },
    ]

    return {
      avatarText,
      roleOptions,
      loginOptions,
      permissionsData,
      loading,
      loader,
      refInputEl,
      previewEl,
      headingArray,
      // inputImageRenderer,
      permissions,
      loginOptionsArgs,
      requestArgs,
      userData,
      refFormObserver,
      getValidationState,
      resetForm,
      BFormInvalidFeedback,
      role,
      typeOptions,
      apiTokenRevokeOptions,
    }
  },
  watch: {
    selected: {
      deep: true,
      handler(newValue) {
        console.log('Selected users changed', newValue)
      },
    },
    role: {
      handler() {
        this.getRolePermissions()
      },
    },
  },
  created() {
    this.getUserMultipleOptions()
    this.getPermissions()
    // console.log('--->', this.userType.data.user_type)
  },
  methods: {
    onSubmit() {
      this.loader = true
      const USER_APP_STORE_MODULE_NAME = 'app-user'

      // Register module
      if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) store.registerModule(USER_APP_STORE_MODULE_NAME, userStoreModule)
      const permissions = []
      this.headingArray.forEach(heading => {
        // console.log('heading', heading)
        const result = this.selected.filter(word => word.includes(heading))
        const assignedPermissions = []
        result.forEach(item => {
          const ret = item.replace(`${heading}_`, '')
          assignedPermissions.push(ret)
        })
        permissions.push({ assignedPermissions, heading })
      })
      this.userData.role = this.role
      const data = { ...this.userData, permissions }
      store.dispatch('app-user/addUser', data)
        .then(() => {
          // console.log('done', response)
          this.$router.replace('/users/list')
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'User Added',
                  icon: 'CoffeeIcon',
                  variant: 'success',
                  text: 'User successfully Added!',
                },
              })
            })
        })
        .catch(error => {
          this.alertErrors = error.data.message
          console.log('error**', this.alertErrors)
          this.loader = false
        })
    },
    userIsSelected(checkBox) {
      return this.selected.includes(checkBox)
    },
    async getPermissions() {
      const newArr = []
      axios
        .get('/role-management/default-permissions')
        .then(response => {
          const headingData = Object.keys(response.data.data)
          this.headingArray = headingData
          let newObj = null
          headingData.forEach(element => {
            newObj = response.data.data[element]
            delete newObj.model
            newArr.push(newObj)
          })
          this.permissionsData = newArr
          this.loader = false
          // console.log('new_Arr', this.permissionsData)
        })
        .catch(error => console.log('ERROR', error))
    },

    async getUserMultipleOptions() {
      try {
        await Promise.all(
          this.requestArgs
            .map(REQUEST => axios
              .get(REQUEST.request)
              .then(res => {
                if (REQUEST.param === 'loginOptions') {
                  this.loginOptions = [...res.data]
                }
                if (REQUEST.param === 'roleOptions') {
                  this.roleOptions = [...res.data.data]
                }
              })),
        )
          .then(() => {
            this.loading = false
          })
      } catch (error) {
        console.log('options error', error)
      }
    },
    checkUsername() {
      // console.log(this.userData.username)
      axios.post('users/validate-username', { username: this.userData.username })
        .then(response => {
          console.log(response.data.status)
          if (response.data.status !== 200) {
            console.log(response.data.message)
          }
        }).catch(error => console.log(error))
    },
    async getRolePermissions() {
      const permissions = await axios.get('role-management/get-role-abilities', { params: { role: this.role } })
      if (permissions.status === 200) {
        this.selected = permissions.data.data
      }
    },
    characterLeft(event) {
      this.maxCharacters = this.maxCharactersAllowed - event.length
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
.spinner-border{
  width: 6rem;
  height: 6rem;
}
</style>
